import React, { useContext, useEffect, useState } from 'react'
import { ClientDto } from 'api/Clients/Types/Client'
import { WalletType } from 'api/Transaction/Types/Transaction'
import { RestrictionDto } from 'api/Wallet/Types/RestrictionDto'
import { RestrictionTypes } from 'api/Wallet/Types/RestrictionTypes'
import { WalletDto } from 'api/Wallet/Types/Wallet'
import { WalletToWalletTransfer } from 'api/Wallet/Types/WalletToWalletTransfer'
import { Spinner } from 'components/Spinner'
import { ToastContext } from 'components/Toast/Toast'
import { useApiClient } from 'utils/api/ApiClient'
import { ExchangeRatesApiClient } from 'utils/api/exchangeRatesApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'

import { TransferForm } from './TransferForm'

interface Props {
  client: ClientDto | null
  preSelectedWallet?: WalletDto

  setClient(): void
}

const filterFromRestrictions = (restriction: RestrictionDto) =>
  ![RestrictionTypes.TransferTo].includes(restriction.id)
const filterToRestrictions = (restriction: RestrictionDto) =>
  ![RestrictionTypes.TransferFrom].includes(restriction.id)

const useFormatter = (props: {
  selectedDebitWallet?: WalletDto
  selectedCreditWallet?: WalletDto
}) => {
  const fromRestrictions =
    props?.selectedDebitWallet?.restrictions.filter(filterFromRestrictions) || []
  const toRestrictions =
    props?.selectedCreditWallet?.restrictions.filter(filterToRestrictions) || []

  return {
    debitTooltipTitle: fromRestrictions.map((restriction) => restriction.name).join(', '),
    hasDebitRestrictions: fromRestrictions?.length > 0,
    creditTooltipTitle: toRestrictions?.map((restriction) => restriction.name).join(', '),
    hasCreditRestrictions: toRestrictions?.length > 0,
  }
}

const TransferPage: React.FC<Props> = ({ client, preSelectedWallet, setClient }) => {
  const setToast = useContext(ToastContext)
  const walletsApiClient = useApiClient(WalletsApiClient)

  const [selectedDebitWallet, setSelectedDebitWallet] = useState<WalletDto>()
  const [selectedCreditWallet, setSelectedCreditWallet] = useState<WalletDto>()
  const [exchangeRate, setExchangeRate] = useState<number>(0.0)
  const exchangeRatesApiClient = useApiClient(ExchangeRatesApiClient)

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(async () => {
    if (!client) {
      return undefined
    }
    return walletsApiClient.getClientWallet(client.id)
  }, [client])

  const setRates = async (type: WalletType) => {
    if (selectedDebitWallet && selectedCreditWallet) {
      let result

      const baseWalletTypeId = selectedDebitWallet.walletType.id
      const quoteWalletTypeId = selectedCreditWallet.walletType.id

      if (type === WalletType.CREDIT) {
        result = await exchangeRatesApiClient.getLatestExchangeRate(
          selectedDebitWallet.currency.id,
          selectedCreditWallet.currency.id,
          null,
          baseWalletTypeId,
          quoteWalletTypeId
        )
      } else {
        result = await exchangeRatesApiClient.getLatestExchangeRate(
          selectedCreditWallet.currency.id,
          selectedDebitWallet.currency.id,
          'sell',
          baseWalletTypeId,
          quoteWalletTypeId
        )
      }
      setExchangeRate(result.rate)
    }
  }

  const { data: wallets, isLoading } = useFetchOne(fetchData)

  useEffect(() => {
    if (preSelectedWallet) {
      onSelectDebitWallet(preSelectedWallet.id)
    }
  }, [wallets, preSelectedWallet]) // eslint-disable-line react-hooks/exhaustive-deps

  const { hasCreditRestrictions, hasDebitRestrictions, debitTooltipTitle, creditTooltipTitle } =
    useFormatter({
      selectedDebitWallet,
      selectedCreditWallet,
    })

  const onSubmit = async (values: WalletToWalletTransfer) => {
    const response = await walletsApiClient.createWalletToWalletTransfer(values)
    setToast({
      type: 'success',
      requireInteraction: true,
      title: `Transfer has been submitted.`,
      subtitle: 'Click for details',
      url: '/transactions/' + response.id,
    })
    forceRefresh()
  }

  const onSelectDebitWallet = (walletId: string) => {
    const walletDto = (wallets || []).find((x) => x.id === walletId)
    setSelectedDebitWallet(walletDto)
    setExchangeRate(0.0)
    return setSelectedCreditWallet(undefined)
  }

  const onSelectCreditWallet = (walletId: string) => {
    const walletDto = (wallets || []).find((x) => x.id === walletId)
    return setSelectedCreditWallet(walletDto)
  }

  const getCreditWalletBasedOnTheDebitOne = (walletDtos: WalletDto[]) => {
    // id 10 means wallet type IB FX/CFD which is accepted
    return walletDtos.filter((wallet) => wallet.id !== selectedDebitWallet?.id)
  }

  const resetAll = () => {
    setSelectedDebitWallet(undefined)
    setSelectedCreditWallet(undefined)
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <TransferForm
      exchangeRate={exchangeRate}
      onSelectDebitWallet={onSelectDebitWallet}
      onSelectCreditWallet={onSelectCreditWallet}
      setRates={setRates}
      selectedDebitWallet={selectedDebitWallet}
      selectedCreditWallet={selectedCreditWallet}
      hasCreditRestrictions={hasCreditRestrictions}
      hasDebitRestrictions={hasDebitRestrictions}
      debitTooltipTitle={debitTooltipTitle}
      creditTooltipTitle={creditTooltipTitle}
      client={client}
      setClient={setClient}
      wallets={wallets || []}
      creditWallets={getCreditWalletBasedOnTheDebitOne(wallets || [])}
      onSubmit={onSubmit}
      resetAll={resetAll}
    />
  )
}

export default TransferPage
